import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PaperPlaneRight, ChatCircleDots, ArrowLeft } from "@phosphor-icons/react";
import UserBadge from "@/components/UserBadge";

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

function Avatar({ user, size = 40 }) {
  const src = resolveImg(user?.avatar_url);
  const letter = (user?.username || "?").slice(0, 1).toUpperCase();
  return src ? (
    <img src={src} alt={user?.username} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" />
  ) : (
    <div style={{ width: size, height: size }} className="rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center font-black shrink-0 text-sm">
      {letter}
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString();
}

export default function Messages() {
  const { user } = useAuth();
  const { uid } = useParams();
  const nav = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [thread, setThread] = useState(null); // { other_user, messages, conversation_id }
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const bottomRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/conversations");
      setConversations(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  const loadThread = useCallback(async (otherId) => {
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/messages/thread/${otherId}`);
      setThread(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setThread(null);
    } finally {
      setLoadingThread(false);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => {
    if (uid) loadThread(uid);
    else setThread(null);
  }, [uid, loadThread]);

  // Poll for new messages every 8s when a thread is open
  useEffect(() => {
    if (!uid) return;
    const t = setInterval(() => loadThread(uid), 8000);
    return () => clearInterval(t);
  }, [uid, loadThread]);

  // Auto-scroll to bottom when messages arrive
  useEffect(() => {
    if (thread?.messages?.length) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [thread?.messages?.length]);

  async function send(e) {
    e.preventDefault();
    if (!uid || !body.trim()) return;
    setSending(true);
    try {
      const trimmed = body.trim();
      setBody("");
      const { data } = await api.post(`/messages/thread/${uid}`, { body: trimmed });
      setThread((t) => t ? { ...t, messages: [...(t.messages || []), data], conversation_id: t.conversation_id || data.conversation_id } : t);
      loadConversations();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <DashboardLayout>
      <div className="mb-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Inbox</div>
        <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
          Messages
        </h1>
      </div>

      <div className="grid md:grid-cols-3 gap-4 h-[calc(100vh-260px)] min-h-[520px]">
        {/* Conversation list */}
        <aside className={`md:col-span-1 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-y-auto ${uid ? "hidden md:block" : ""}`} data-testid="messages-conversation-list">
          {conversations.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500">
              <ChatCircleDots size={40} weight="duotone" className="mx-auto mb-2 text-slate-400" />
              No conversations yet.
              <div className="mt-2">
                <Link to="/discover" className="text-blue-700 dark:text-blue-400 font-bold hover:underline">Find people to message →</Link>
              </div>
            </div>
          )}
          {conversations.map((c) => {
            const isActive = uid === c.other_user.id;
            const preview = c.last_sender_id === user?.id ? `You: ${c.last_message_preview}` : c.last_message_preview;
            return (
              <button
                key={c.id}
                data-testid={`conv-${c.other_user.id}`}
                onClick={() => nav(`/messages/${c.other_user.id}`)}
                className={`w-full text-left px-3 py-3 flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors duration-200 ${isActive ? "bg-blue-50 dark:bg-blue-950/40" : ""}`}
              >
                <Avatar user={c.other_user} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-black text-slate-900 dark:text-white truncate inline-flex items-center gap-1">{c.other_user.username}<UserBadge user={c.other_user} size={12} /></div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400 shrink-0">{formatTime(c.last_message_at)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm text-slate-600 dark:text-slate-300 truncate flex-1">{preview || <em className="text-slate-400">Say hi 👋</em>}</div>
                    {c.unread_count > 0 && (
                      <span className="bg-blue-600 text-white text-[10px] font-black rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center shrink-0" data-testid={`conv-unread-${c.other_user.id}`}>
                        {c.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </aside>

        {/* Thread pane */}
        <section className={`md:col-span-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col ${uid ? "" : "hidden md:flex"}`}>
          {!uid && (
            <div className="flex-1 flex items-center justify-center text-center p-6">
              <div>
                <ChatCircleDots size={56} weight="duotone" className="mx-auto text-slate-300 dark:text-slate-600" />
                <div className="mt-3 text-lg font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Pick a conversation</div>
                <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">Or start a new one from someone's profile.</div>
              </div>
            </div>
          )}

          {uid && (
            <>
              <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
                <button className="md:hidden h-8 w-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 inline-flex items-center justify-center" onClick={() => nav("/messages")} aria-label="Back">
                  <ArrowLeft size={18} weight="bold" />
                </button>
                {thread?.other_user && (
                  <Link to={`/u/${thread.other_user.id}`} className="flex items-center gap-3 hover:opacity-90">
                    <Avatar user={thread.other_user} />
                    <div>
                      <div className="font-black text-slate-900 dark:text-white inline-flex items-center gap-1">{thread.other_user.username}<UserBadge user={thread.other_user} size={14} /></div>
                      {thread.other_user.membership && thread.other_user.membership !== "Free" && (
                        <div className="text-[11px] text-yellow-600 font-bold uppercase tracking-widest">{thread.other_user.membership}</div>
                      )}
                    </div>
                  </Link>
                )}
              </header>

              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="messages-thread">
                {loadingThread && !thread && <div className="text-sm text-slate-500">Loading…</div>}
                {thread?.messages?.length === 0 && (
                  <div className="text-center text-sm text-slate-500 dark:text-slate-400 py-8">
                    No messages yet — send the first one!
                  </div>
                )}
                {thread?.messages?.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-blue-700 text-white rounded-br-sm" : "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-bl-sm"}`}>
                        <div className="text-[15px] whitespace-pre-wrap break-words">{m.body}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-slate-500 dark:text-slate-400"}`}>{formatTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              <form onSubmit={send} className="border-t border-slate-200 dark:border-slate-800 p-3 flex items-center gap-2">
                <input
                  data-testid="messages-input"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Write a message…"
                  maxLength={2000}
                  className="flex-1 rounded-full bg-slate-100 dark:bg-slate-800 border border-transparent focus:border-blue-500 focus:outline-none px-4 py-2 text-sm text-slate-900 dark:text-slate-100"
                />
                <button
                  type="submit"
                  data-testid="messages-send-btn"
                  disabled={sending || !body.trim()}
                  className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white transition-colors duration-200"
                  aria-label="Send"
                >
                  <PaperPlaneRight size={16} weight="fill" />
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}
